"use client";
import Image from "next/image";
import Link from "next/link";
import { getStrapiMedia } from "../lib/api-helpers";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

interface FeaturesProps {
  data: {
    headingSectionsFeatures: string;
    descriptionSectionsFeatures: string;
    feature: Feature[];
  };
}

interface Picture {
  data: {
    id: string;
    attributes: {
      url: string;
      name: string;
      alternativeText: string;
    };
  };
}

interface Feature {
  title: string;
  description: string;
  showLink: boolean;
  newTab: boolean;
  url: string;
  text: string;
  colour: string;
  media: Picture;
}

function Feature({
  title,
  description,
  media,
  showLink,
  url,
  text,
}: Feature) {
  const imgUrl = getStrapiMedia(media.data?.attributes?.url);

  const [hover, setHover] = useState(false);

  return (
    <Link href={url} target="_blank" className="w-full">
      <div className="flex justify-center w-full">
        <div
          className="relative flex items-center justify-center w-full h-full"
        >

          <div className="flex flex-col items-start justify-center w-full">
            <Image
                src={imgUrl ?? ''}
                alt={media?.data?.attributes.alternativeText || description}
                className="object-contain mx-auto w-full h-[300px] 2xl:h-[344px]"
                width={540}
                height={540}
              /> 
            <div className="flex flex-col justify-center align-center w-full text-center pt-[32px] md:pt-[40px]">
              <h3
                className="!leading-[1.45] text-2xl lg:text-lg text-[#001630] mx-auto"
              dangerouslySetInnerHTML={{__html: title}}
              />
              <div className="flex justify-center items-center pt-[16px]">
                {showLink && url && text && (
                  <div className={`fit-content flex items-center gap-2 text-[#001630] text-lg md:text-base font-semibold rounded-xl px-[16px] py-[12px]`} 
                  onMouseOver={()=> setHover(true)} 
                  onMouseOut={()=> setHover(false)}>                
                    <Image
                      src={hover ? '/images/learn-hover.webp' : '/images/learn-default.webp'}
                      alt={`${description} Learn More`}
                      className="object-contain mx-auto h-[48px] 2xl:h-[54px]"
                      width={160}
                      height={48}
                      loading="lazy"
                    /> 
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function Features({ data }: FeaturesProps) {
  const featureRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    if(featureRefs.current){
      gsap.fromTo(
      featureRefs.current,
      { y: 100, opacity: 0 },
      {
        y: 0,
        opacity: 1,
        stagger: 0.25,
        duration: 0.8,

        ease: "power3.out",

        scrollTrigger: {
          trigger: featureRefs.current,
          toggleActions: "restart none none reverse",

          start: "top 70%", // start the animation when the feature enters the viewport
        },
      }
    );
    }
    
  }, []);
  return (
    <div className="relative flex items-center justify-center overflow-hidden py-[48px] md:py-[96px] bg-[#99ADFC1A]">
      <div className="container flex flex-col justify-center  mx-auto lg:!mt-0  px-[24px] xl:px-0 relative z-20 feature">
        <h2 className="text-[#032d60] text-center !leading-[1.2] text-6xl md:text-5xl 2xl:text-6xl font-bold sm:w-8/12 lg:w-11/12 mx-auto pb-[22px] mb:pb-[36px] lg:pb-[72px]">
            {data.headingSectionsFeatures}
          </h2>
          {data.descriptionSectionsFeatures && (
            <p className="">{data.descriptionSectionsFeatures}</p>
          )}
            <div className="grid grid-cols-1 gap-10 sm:grid-cols-2 lg:grid-cols-3 lg:gap-1 content-center">
              {data.feature.map((feature: Feature, index: number) => (
                <div
                  className="flex"
                  ref={(el) => { featureRefs.current[index] = el; }}
                  key={index}
                >
                  <Feature key={index} {...feature} />
                </div>
              ))}
            </div>
      </div>
    </div>
  );
}